/**
 * @fileoverview The share studio's screens' parts: each screen's top (close,
 * its name, "3 of 6", the six dashes), Pick's grid, and the bands of choices
 * under the picture on Layout (the layouts and Card's ground), Size (the four
 * sizes and where each is for), Details (what is printed, the date's Day and
 * Year, which handle, the text size) and Crop (the photographs going out, each
 * opening Move & zoom). Moved out of
 * plant-list/components/dialogs/SharePlantDialog (batch 18, A105) as
 * components; nothing drawn changed.
 *
 * Where: SharePlantDialog.
 * Words (en/es): plants.share.* (steps.*, photosOf, picture, photo, layout,
 * needTwo, hints.*, layouts.*, background, grounds.*, size, sizes.*, where.*,
 * textSize, textSmaller, textLarger, el.*, dateParts, whichHandle, handleSavedToast),
 * locations.share.* (cover, on.*, el.*), seasonReview.sheet.card, sitewide.close.
 *
 * @module ShareStepBands
 */

import React from 'react';
import { Sprout, Crop } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { CloseX } from '@/shared/components/ui/CloseX';
import { DialogTitle } from '@/components/ui/dialog';
import { Chip } from '@/shared/components/ui/Chip';
import { useTendingFor } from '@/features/billing/lib/useBilling';
import { ProfileLinksScreen } from '@/user/components/ProfileLinksCard';
import { useToast } from '@/shared/components/ui/use-toast';
import { linksOf } from '@/shared/lib/socialLinks';
import { handleChipState } from './handleChip';
import { formatDate, parseDate } from '@/shared/utils/dateUtils';
import type { PlantPhoto } from '@/shared/types/photo';
import {
  groundColours, layoutAvailable, CARD_GROUND_VALUES, SHARE_LAYOUT_VALUES, SHARE_SIZE_VALUES, SHARE_SIZES, TEXT_SCALES,
  type ShareElements,
} from '@/features/share/frames/frameOptions';
import { SHARE_STEPS, type ShareStep } from '@/features/plant-list/lib/shareDraft';
import { SocialIcon } from '@/shared/components/SocialIcon';
import { COLLAGE_ID, COVER_KEYS, ELEMENT_KEYS, PLANT_KEYS, type Editing, type SharePlantDialogProps } from './shareStudioTypes';
import type { ShareStory } from './useShareStory';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';

/**
 * The smallest picture of a photograph there is, for the strip.
 *
 * @param photo - The photograph.
 */
const thumbOf = (photo: PlantPhoto) => photo.thumbUrl || photo.signed_photo_url || photo.cardUrl;

/** What a band is given: the story and the choices (useShareStory). */
interface BandProps {
  /** The story and the choices. */
  story: ShareStory;
}

/** What a screen's top is given. */
interface StepHeaderProps {
  /** The screen's name. */
  heading: string;
  /** The screen that is up. */
  step: ShareStep;
  /** Closes the sheet and forgets the share in progress. */
  closeSheet: () => void;
  /** The words, in the reader's language. */
  t: ShareStory['t'];
}

/**
 * A step's top, the same on every screen: close at the left, the
 * screen's name, "3 of 6" at the right, and the six dashes under it, the
 * ones reached filled. Close forgets the share in progress (closeSheet),
 * like every other way out.
 *
 * @param props - StepHeaderProps.
 */
export const StepHeader: React.FC<StepHeaderProps> = ({ heading, step, closeSheet, t }) => {
  /** Where this screen is among the steps, from 0: the dashes under the title and "3 of 6". */
  const stepIndex = SHARE_STEPS.indexOf(step);
  return (
    <div className="shrink-0 border-b px-2 pb-1.5 pt-2">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center">
        <div className="justify-self-start">
          <CloseX onClick={closeSheet} label={t('sitewide.close')} />
        </div>
        <DialogTitle className="truncate px-1 text-center text-lg">{heading}</DialogTitle>
        <span className="justify-self-end pr-2 text-sm tabular-nums text-muted-foreground" aria-hidden="true">
          {t('plants.share.steps.counter', { n: stepIndex + 1, total: SHARE_STEPS.length })}
        </span>
      </div>
      <ol className="mt-1 flex justify-center gap-1.5" aria-label={t('plants.share.steps.of', { n: stepIndex + 1, total: SHARE_STEPS.length })}>
        {SHARE_STEPS.map((each, index) => (
          <li key={each} aria-current={each === step ? 'step' : undefined} className={cn('h-1 w-5 rounded-full', index <= stepIndex ? 'bg-primary' : 'bg-muted-foreground/25')}>
            <span className="sr-only">{t(`plants.share.steps.${each}`)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

/**
 * Pick's grid: every photograph, three across, tapped to number it (the
 * order it goes out in) and tapped again to take it out.
 *
 * @param props.dialog - The dialog's props (the season, its plants, the subject's names).
 * @param props.story - The story and the choices.
 */
export const PickGrid: React.FC<BandProps & { dialog: SharePlantDialogProps }> = ({ dialog, story }) => {
  const { name, variety, plantOf, season } = dialog;
  const { t, isLocation, ordered, cardPhoto, photosOnly, picked, maxPhotos, isCard, pickPhoto } = story;

  /**
   * The photographs on Pick, newest first: a location's as given (its cover
   * first), a season's card then its photographs.
   */
  const pickList = isLocation ? ordered : cardPhoto ? [cardPhoto, ...[...photosOnly].reverse()] : [...ordered].reverse();
  /**
   * Pick's grid: every photograph, three across, tapped to number it (the
   * order it goes out in) and tapped again to take it out.
   */
  return (
    <ul className="grid min-h-0 flex-1 grid-cols-3 gap-0.5 overflow-y-auto p-0.5 sm:grid-cols-5" aria-label={t('plants.share.photosOf', { plant: variety })}>
      {pickList.map((photo) => {
        const at = picked.indexOf(photo.photo_id);
        const full = at < 0 && maxPhotos !== undefined && picked.length >= maxPhotos;
        const card = isCard(photo);
        // A location's or a season's: its plants' photos each named on the tile.
        /** The plant a season or location picture belongs to, if any. */
        const plant = (isLocation || season) && photo.photo_id < 0 ? plantOf?.(photo.photo_id) ?? null : null;
        /** The words under a picture. */
        const label = (isLocation || season)
          ? (plant ? plant.variety || plant.name : card ? t('seasonReview.sheet.card') : photo.photo_id === 0 ? t('locations.share.cover') : name)
          : null;
        return (
          <li key={photo.photo_id}>
            <button
              type="button"
              onClick={() => pickPhoto(photo.photo_id)}
              disabled={full}
              aria-pressed={at >= 0}
              aria-label={`${card ? t('seasonReview.sheet.card') : photo.date_taken ? formatDate(parseDate(photo.date_taken)) : t('plants.share.photo')}${at >= 0 ? `, ${t('plants.share.picture', { n: at + 1, total: picked.length })}` : ''}`}
              className={cn(
                'relative block aspect-square w-full overflow-hidden',
                full && 'cursor-not-allowed opacity-40',
              )}
            >
              {card && (
                <span className="absolute inset-0 flex items-center justify-center bg-primary/10 text-primary" aria-hidden="true">
                  <Sprout className="h-8 w-8" />
                </span>
              )}
              {thumbOf(photo) && <FramedPhoto src={photo.cardUrl || thumbOf(photo)} alt="" draggable={false} />}
              {/* Picked: a green inset ring and its number; not yet: an empty ring. */}
              {at >= 0 && <span className="pointer-events-none absolute inset-0 ring-[3px] ring-inset ring-primary" aria-hidden="true" />}
              {at >= 0 ? (
                <span className="absolute right-1.5 top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1 text-[13px] font-bold text-primary-foreground ring-2 ring-white">{at + 1}</span>
              ) : (
                <span className="absolute right-1.5 top-1.5 h-6 w-6 rounded-full border-2 border-white/90 bg-black/20 shadow" aria-hidden="true" />
              )}
              {(label || (!card && photo.date_taken)) && (
                <span className="absolute inset-x-0 bottom-0 truncate bg-black/50 px-1.5 text-[11px] leading-5 text-white">
                  {label ?? parseDate(photo.date_taken).toFormat('d LLL')}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

/**
 * Layout's band: the layouts as chips, the one chosen filled, with Card's ground under them.
 *
 * @param props.story - The story and the choices.
 */
export const LayoutBand: React.FC<BandProps> = ({ story }) => {
  const { t, layout, photosOnly, set, style, update, setShown } = story;

  /** The layouts as chips, the one chosen filled, with Card's ground under them. */
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t('plants.share.layout')}>
        {SHARE_LAYOUT_VALUES.map((value) => (
          <Chip
            key={value}
            pressed={layout === value}
            disabled={!layoutAvailable(value, photosOnly.length)}
            disabledReason={t('plants.share.needTwo')}
            title={t(`plants.share.hints.${value}`)}
            onClick={() => {
              update({ layout: value });
              // Choosing the collage shows it, the picture that goes first.
              if (value === 'collage' && set.length >= 2) setShown(COLLAGE_ID);
            }}
          >
            {t(`plants.share.layouts.${value}`)}
          </Chip>
        ))}
      </div>
      {layout === 'card' && (
        <div className="flex gap-1.5 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }} role="radiogroup" aria-label={t('plants.share.background')}>
          {CARD_GROUND_VALUES.map((ground) => {
            const on = style.cardGround === ground;
            return (
              <button
                key={ground}
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={t(`plants.share.grounds.${ground}`)}
                title={t(`plants.share.grounds.${ground}`)}
                onClick={() => update({ cardGround: ground })}
                className={cn(
                  'h-7 w-10 shrink-0 rounded-md border border-black/10 transition-shadow',
                  on && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
                )}
                style={{ background: groundColours(ground).fill }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Size's band: the sizes, as shapes with their names, and where the chosen one is for.
 *
 * @param props.story - The story and the choices.
 */
export const SizeBand: React.FC<BandProps> = ({ story }) => {
  const { t, style, update } = story;

  /** The sizes, as shapes with their names, and where the chosen one is for. */
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-1.5" role="group" aria-label={t('plants.share.size')}>
        {SHARE_SIZE_VALUES.map((value) => {
          const dims = SHARE_SIZES[value];
          return (
            <Chip
              key={value}
              pressed={style.size === value}
              title={t(`plants.share.where.${value}`)}
              onClick={() => update({ size: value })}
              icon={<SizeGlyph w={dims.w} h={dims.h} />}
              className="w-full px-2"
            >
              {t(`plants.share.sizes.${value}`)}
            </Chip>
          );
        })}
      </div>
      <p className="text-sm text-muted-foreground">{t(`plants.share.where.${style.size}`)}</p>
    </div>
  );
};

/**
 * Details' band: what is printed, the date's parts, which handle, and the text size.
 *
 * @param props.story - The story and the choices.
 */
export const DetailsBand: React.FC<BandProps> = ({ story }) => {
  const {
    t, isLocation, style, update, stepText, setElement, scaleStep, dateDay, dateYear, links, chosenLink, handle,
    coverElements, setCoverElements, plantElements, setPlantElements,
  } = story;
  /** A member tending someone else's garden: the links are the owner's, so "Your handle" cannot set one up for them (B37). */
  const tending = useTendingFor() !== null;
  /** Whether the profile's links screen is open over the share (B85: no handle yet, so "Your handle" sets one up). */
  const [setupOpen, setSetupOpen] = React.useState(false);
  /** Switches the handle on in the group whose chip was tapped, once a handle is saved. */
  const turnHandleOn = React.useRef<(() => void) | null>(null);
  const { toast } = useToast();

  /** The lettering a step smaller or larger. */
  const textSizeGroup = (
    <div role="group" aria-label={t('plants.share.textSize')} className="flex shrink-0 overflow-hidden rounded-md border bg-background">
      <Button
        type="button"
        variant="ghost"
        onClick={() => stepText(-1)}
        disabled={scaleStep <= 0}
        aria-label={t('plants.share.textSmaller')}
        className="h-8 w-10 rounded-none px-0 font-semibold disabled:opacity-35"
      >
        <span className="text-[0.7rem]">A</span>−
      </Button>
      <Button
        type="button"
        variant="ghost"
        onClick={() => stepText(1)}
        disabled={scaleStep >= TEXT_SCALES.length - 1}
        aria-label={t('plants.share.textLarger')}
        className="h-8 w-10 rounded-none border-l px-0 font-semibold disabled:opacity-35"
      >
        A+
      </Button>
    </div>
  );
  /**
   * A chip that turns one printed thing on or off.
   *
   * @param key - What is printed.
   * @param on - Whether it is printed now.
   * @param label - The chip's words.
   * @param toggle - Turns it the other way.
   * @param setOn - Switches it on; the handle's chip uses it after a handle is set up here.
   */
  const elementChip = (key: string, on: boolean, label: React.ReactNode, toggle: () => void, setOn?: () => void) => {
    const state = key === 'handle' ? handleChipState(on, Boolean(handle), tending) : { disabled: false, opensSetup: false, pressed: on };
    // With one link the handle's chip shows it; with more, the chip is the plain switch ("Your handle")
    // and the links after it choose which, so the chosen one is not shown twice (owner, 2026-10-15:
    // "There's two handles somehow, and it's confusing").
    const showsHandle = key === 'handle' && handle && links.length <= 1;
    return (
      <Chip
        key={key}
        pressed={state.pressed}
        disabled={state.disabled}
        onClick={state.opensSetup ? () => { turnHandleOn.current = setOn ?? null; setSetupOpen(true); } : toggle}
        icon={showsHandle ? <SocialIcon name={handle.place} size={13} /> : undefined}
      >
        {showsHandle ? handle.text : label}
      </Chip>
    );
  };
  /** The date's parts, after its chip while a date is printed: its day and its year can be left out ("January 2024", "January 21"). */
  const dateChips = (
    <span className="flex shrink-0 items-center gap-1.5" role="group" aria-label={t('plants.share.dateParts')}>
      {elementChip('dateDay', dateDay, t('plants.share.el.dateDay'), () => update({ dateDay: !dateDay }))}
      {elementChip('dateYear', dateYear, t('plants.share.el.dateYear'), () => update({ dateYear: !dateYear }))}
    </span>
  );
  /** Which handle is printed, after the handle's chip while one is and the account has more than one link to choose from. */
  const handleChoices = links.length > 1 ? (
    <span className="flex shrink-0 items-center gap-1.5" role="radiogroup" aria-label={t('plants.share.whichHandle')}>
      {links.map((each) => (
        <Chip
          key={each.key}
          radio
          pressed={chosenLink?.key === each.key}
          onClick={() => update({ handle: each.key })}
          icon={<SocialIcon name={each.key} size={13} />}
        >
          <span className="max-w-[9rem] truncate">{each.handle}</span>
        </Chip>
      ))}
    </span>
  ) : null;
  /**
   * Details: what is printed, as chips that wrap (each printed thing, the
   * date's Day and Year and which handle after their chips, then the text
   * size). A location's printed things are two sets, its own pictures' and
   * its plants' photos', a row each. No words are printed (owner,
   * 2026-10-15), so there is no box for them.
   */
  return (
    <div className="space-y-2">
      {/* The profile's own links screen, over the share: a save closes it and prints the new handle; closing leaves the share as it was.
          Only a save that left a handle the server will print turns it on and says, in a short toast, that it can be changed any
          time in Profile (E7); a save with every box empty just closes. */}
      <ProfileLinksScreen
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onSaved={(saved) => {
          setSetupOpen(false);
          if (linksOf(saved).length === 0) return;
          turnHandleOn.current?.();
          toast({ description: t('plants.share.handleSavedToast') });
        }}
      />
      {isLocation ? (
        <>
          {([['cover', COVER_KEYS, coverElements, setCoverElements], ['plants', PLANT_KEYS, plantElements, setPlantElements]] as const).map(([group, keys, els, setEls]) => (
            <div key={group} className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t(`locations.share.on.${group}`)}>
              <span className="shrink-0 text-xs text-muted-foreground" aria-hidden="true">{t(`locations.share.on.${group}`)}</span>
              {keys.map((key) => (
                <React.Fragment key={key}>
                  {elementChip(key, Boolean(els[key]), t(`locations.share.el.${group}.${key}`), () => setEls((current: ShareElements) => ({ ...current, [key]: !els[key] })), () => setEls((current: ShareElements) => ({ ...current, [key]: true })))}
                  {key === 'date' && els.date && dateChips}
                  {/* The handle choices once, after the handle chip that is on: the cover's first. */}
                  {key === 'handle' && els.handle && (group === 'cover' || !coverElements.handle) && handleChoices}
                </React.Fragment>
              ))}
            </div>
          ))}
          {textSizeGroup}
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {ELEMENT_KEYS.filter((key) => key !== 'words').map((key) => (
            <React.Fragment key={key}>
              {elementChip(key, style.elements[key], t(`plants.share.el.${key}`), () => setElement(key, !style.elements[key]), () => setElement(key, true))}
              {key === 'date' && style.elements.date && dateChips}
              {key === 'handle' && style.elements.handle && handleChoices}
            </React.Fragment>
          ))}
          {textSizeGroup}
        </div>
      )}
    </div>
  );
};

/**
 * Crop's band: the photographs going out, each a thumbnail that puts its
 * picture in view and opens Move & zoom for it at the frame's shape.
 *
 * @param props.story - The story and the choices.
 * @param props.slotAspectOf - The shape of the slot a photograph fills in the size and layout chosen (useShareFrames): the crop box's.
 * @param props.setEditing - Opens Move & zoom (usePreviewGestures).
 */
export const CropBand: React.FC<BandProps & { slotAspectOf: (photo: PlantPhoto) => number; setEditing: (editing: Editing) => void }> = ({ story, slotAspectOf, setEditing }) => {
  const { t, layout, set, shownPhoto, setShown } = story;

  /** The photographs that can be moved in their frames: every one going out, but none in Before & after, which has two to a picture. */
  const croppable = layout === 'ba' ? [] : set;
  /**
   * Crop: the photographs going out, each a thumbnail that puts its picture
   * in view and opens Move & zoom for it at the frame's shape.
   */
  return croppable.length === 0 ? (
    <p className="text-sm text-muted-foreground">{t('plants.share.steps.cropNone')}</p>
  ) : (
    <div>
      {/* No line over them (owner, 2026-10-15): each says what it does with a crop mark, and in its name. */}
      <ul className="flex flex-wrap gap-2">
        {croppable.map((photo, index) => (
          <li key={photo.photo_id}>
            <button
              type="button"
              onClick={() => { setShown(photo.photo_id); setEditing({ photo, aspect: slotAspectOf(photo) }); }}
              aria-label={`${t('plants.share.steps.cropHint')} · ${t('plants.share.picture', { n: index + 1, total: croppable.length })}`}
              title={t('plants.share.steps.cropHint')}
              className={cn(
                'relative block h-14 w-14 overflow-hidden rounded-md ring-offset-2 ring-offset-background',
                shownPhoto?.photo_id === photo.photo_id && 'ring-2 ring-primary',
              )}
            >
              {thumbOf(photo) && <FramedPhoto src={thumbOf(photo)} alt="" draggable={false} />}
              <span className="absolute bottom-0.5 right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white" aria-hidden="true">
                <Crop className="h-3 w-3" />
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * A size as its own shape, so the three read as shapes before they read as names.
 *
 * @param props.w - The size's width in pixels.
 * @param props.h - Its height in pixels.
 */
const SizeGlyph: React.FC<{ w: number; h: number }> = ({ w, h }) => {
  /** The glyph's longest side in pixels. */
  const box = 20;
  /** The scale that fits the size into the box. */
  const scale = box / Math.max(w, h);
  /** The drawn width. */
  const rw = Math.round(w * scale);
  /** The drawn height. */
  const rh = Math.round(h * scale);
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
      <rect x={(24 - rw) / 2} y={(24 - rh) / 2} width={rw} height={rh} rx="1.5" fill="currentColor" opacity="0.85" />
    </svg>
  );
};
